import type { ShadowComparatorOptions, ShadowComparison, ShadowMessage, ShadowSource } from "./types";

import { diffMessages } from "./diffMessages";

const DEFAULT_MATCH_WINDOW_MS = 10_000;
const DEFAULT_IGNORED_KEYS = ["timestamp"] as const;

interface PendingEntry {
    message: ShadowMessage;
    receivedAt: number;
    timer: ReturnType<typeof setTimeout>;
}

/**
 * Buffers inbound messages from the live GAIA socket and the shadow Simplr socket
 * and pairs them up so their payloads can be compared.
 *
 * Messages are matched FIFO within a key of `name | correlationId | sessionId`.
 * When both sides produce a message for a key, the envelopes are diffed and an
 * `equal` / `different` result is reported. If a message has no counterpart within
 * `matchWindowMs`, a `gaia-only` / `simplr-only` result is reported instead.
 *
 * The comparator is purely observational: it never sends anything and never emits
 * on the GAIA event bus, so it cannot affect the live UI.
 */
export class ShadowComparator {
    private readonly report: (comparison: ShadowComparison) => void;
    private readonly matchWindowMs: number;
    private readonly ignoredKeys: ReadonlySet<string>;
    private readonly pending: Record<ShadowSource, Map<string, PendingEntry[]>> = {
        gaia: new Map(),
        simplr: new Map(),
    };

    constructor({
        report,
        matchWindowMs = DEFAULT_MATCH_WINDOW_MS,
        ignoredKeys = DEFAULT_IGNORED_KEYS,
    }: ShadowComparatorOptions) {
        this.report = report;
        this.matchWindowMs = matchWindowMs;
        this.ignoredKeys = new Set(ignoredKeys);
    }

    record(source: ShadowSource, message: ShadowMessage): void {
        const key = buildMatchKey(message);
        const receivedAt = Date.now();
        const counterpartSource: ShadowSource = source === "gaia" ? "simplr" : "gaia";
        const counterpartQueue = this.pending[counterpartSource].get(key);

        if (counterpartQueue && counterpartQueue.length > 0) {
            const counterpart = counterpartQueue.shift()!;
            clearTimeout(counterpart.timer);
            if (counterpartQueue.length === 0) this.pending[counterpartSource].delete(key);
            this.reportMatch(source, message, receivedAt, counterpart);
            return;
        }

        this.enqueue(source, key, message, receivedAt);
    }

    /** Clears every pending timer. Call on unmount to avoid leaks and late reports. */
    dispose(): void {
        for (const source of ["gaia", "simplr"] as const) {
            for (const queue of this.pending[source].values()) {
                for (const entry of queue) clearTimeout(entry.timer);
            }
            this.pending[source].clear();
        }
    }

    private enqueue(source: ShadowSource, key: string, message: ShadowMessage, receivedAt: number): void {
        const queue = this.pending[source].get(key) ?? [];
        const entry: PendingEntry = {
            message,
            receivedAt,
            timer: setTimeout(() => this.expire(source, key, entry), this.matchWindowMs),
        };
        queue.push(entry);
        this.pending[source].set(key, queue);
    }

    private expire(source: ShadowSource, key: string, entry: PendingEntry): void {
        const queue = this.pending[source].get(key);
        if (queue) {
            const index = queue.indexOf(entry);
            if (index >= 0) queue.splice(index, 1);
            if (queue.length === 0) this.pending[source].delete(key);
        }

        this.report({
            status: source === "gaia" ? "gaia-only" : "simplr-only",
            eventName: entry.message.name,
            correlationId: entry.message.correlationId,
            sessionId: extractSessionId(entry.message),
            diffs: [],
        });
    }

    private reportMatch(
        source: ShadowSource,
        message: ShadowMessage,
        receivedAt: number,
        counterpart: PendingEntry,
    ): void {
        const gaiaMessage = source === "gaia" ? message : counterpart.message;
        const simplrMessage = source === "simplr" ? message : counterpart.message;
        const gaiaAt = source === "gaia" ? receivedAt : counterpart.receivedAt;
        const simplrAt = source === "simplr" ? receivedAt : counterpart.receivedAt;

        const diffs = diffMessages(gaiaMessage, simplrMessage, this.ignoredKeys);
        this.report({
            status: diffs.length > 0 ? "different" : "equal",
            eventName: gaiaMessage.name,
            correlationId: gaiaMessage.correlationId,
            sessionId: extractSessionId(gaiaMessage),
            diffs,
            latencyDeltaMs: simplrAt - gaiaAt,
        });
    }
}

function buildMatchKey(message: ShadowMessage): string {
    return `${message.name}|${message.correlationId ?? ""}|${extractSessionId(message) ?? ""}`;
}

function extractSessionId(message: ShadowMessage): string | undefined {
    const body = message.body as { sessionGroupId?: unknown; sessionId?: unknown } | null | undefined;
    if (!body) return undefined;
    const sessionId = body.sessionGroupId ?? body.sessionId;
    return typeof sessionId === "string" ? sessionId : undefined;
}
